import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Loader from "../components/Loader";
import appConfig from "../../config/appConfig";
import { formatSelectOption } from "../../helpers/function";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { AUTH_TOKEN } from "../../constants/token";

const IFRAME_STYLE = {
  width: "100%",
  height: "calc(100vh - 180px)",
  minHeight: "500px",
  border: 0,
};

const ClaimMetabaseReporting = (props) => {
  const { t, ready } = useTranslation();
  const location = useLocation();



  const userRoles = props.userRoles ?? [];

  const isFilialUser = useMemo(
    () =>
      userRoles.includes("admin-filial") || userRoles.includes("pilot-filial"),
    [userRoles]
  );

  const isHoldingUser = useMemo(
    () =>
      userRoles.includes("admin-holding") ||
      userRoles.includes("pilot-holding"),
    [userRoles]
  );

  const dashboard = useMemo(() => {
    const fromQuery = new URLSearchParams(location.search).get("dashboard");
    return fromQuery || appConfig.metabaseDashboard || "";
  }, [location.search]);

  document.title = "Satis client - " + (ready ? t("Rapport Metabase") : "");

  const allInstitutionsOption = useMemo(
    () => ({ label: t("Toutes les institutions"), value: null }),
    [t]
  );

  const [load, setLoad] = useState(true);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [institutions, setInstitutions] = useState([]);

  const institutionId = useMemo(() => {
    if (isHoldingUser) return selectedInstitution?.value ?? null;
    if (isFilialUser) return props.userInstitutionId ?? null;
    return null;
  }, [
    isHoldingUser,
    isFilialUser,
    props.userInstitutionId,
    selectedInstitution,
  ]);

  useEffect(() => {
    if (!isHoldingUser || !verifyTokenExpire()) return;

    setSelectedInstitution(allInstitutionsOption);

    axios
      .get(`${appConfig.apiDomaine}/my/institutions-whithout-holding`)
      .then((response) => {
        setInstitutions([
          allInstitutionsOption,
          ...formatSelectOption(response.data.institution, "name", false),
        ]);
      })
      .catch((err) => console.error(err));
  }, [isHoldingUser, allInstitutionsOption]);

  const fetchMetabaseReport = useCallback(async () => {
    if (!dashboard) {
      setIframeUrl(null);
      setError(
        t(
          "Identifiant dashboard Metabase manquant. Ajoutez ?dashboard=... dans l'URL ou metabaseDashboard dans la configuration."
        )
      );
      setLoad(false);
      return;
    }

    if (!verifyTokenExpire()) {
      setIframeUrl(null);
      setError(t("Session expirée. Veuillez vous reconnecter."));
      setLoad(false);
      return;
    }

    setLoad(true);
    setError(null);
    axios.defaults.headers.common["Authorization"] = AUTH_TOKEN;

    try {
      const params = { dashboard };

      if (isHoldingUser) {
        if (selectedInstitution?.value != null && selectedInstitution?.value !== "") {
          params.institution_id = selectedInstitution.value;
        }
      } else if (
        isFilialUser &&
        props.userInstitutionId != null &&
        props.userInstitutionId !== ""
      ) {
        params.institution_id = props.userInstitutionId;
      }

      const { data } = await axios.get(
        `${appConfig.apiDomaine}/metabase-report`,
        { params }
      );

      if (!data?.iframeUrl) {
        setIframeUrl(null);
        setError(t("Réponse Metabase invalide : iframeUrl introuvable."));
        return;
      }

      setIframeUrl(data.iframeUrl);
    } catch (err) {
      console.error(err);
      setIframeUrl(null);
      setError(t("Impossible de charger le rapport Metabase."));
    } finally {
      setLoad(false);
    }
  }, [
    dashboard,
    institutionId,
    isFilialUser,
    isHoldingUser,
    props.userInstitutionId,
    selectedInstitution,
    t,
  ]);

  useEffect(() => {
    fetchMetabaseReport();
  }, [fetchMetabaseReport]);

  if (!ready) return null;

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Rapport Metabase")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
              >
                {t("Rapport Metabase")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        {isHoldingUser ? (
          <div className="kt-portlet mb-4">
            <div className="kt-portlet__body">
              <div className="form-group mb-0">
                <label htmlFor="institution">{t("Institution")}</label>
                <Select
                  inputId="institution"
                  value={selectedInstitution ?? allInstitutionsOption}
                  onChange={setSelectedInstitution}
                  options={institutions}
                />
              </div>
            </div>
          </div>
        ) : null}

        {load ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Loader />
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : (
          <iframe
            key={`metabase-${institutionId ?? "all"}`}
            title={t("Rapport Metabase")}
            src={iframeUrl}
            style={IFRAME_STYLE}
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const user = state.user.user ?? {};

  return {
    userRoles:
      user.roles?.map((role) => role.name) ??
      user.data?.roles?.map((role) => role.name) ??
      [],
    userInstitutionId:
      user.institution?.id ?? user.data?.identite?.staff?.institution?.id,
  };
};

export default connect(mapStateToProps)(ClaimMetabaseReporting);
